'use client';

/**
 * ProductQuestions — public Q&A section for a Market product.
 *
 * Spec ref: SPEC-150 (Sprint 74, 2026-09-07) — F-004b: Chat pre-venta.
 *
 * Fetches GET /api/market/products/:productId/questions (public, no auth).
 * Shows list of questions + answers. If logged in, shows input to ask.
 * If seller, shows answer button on unanswered questions.
 *
 * Anti-§6: pure UI — reads from public API.
 */

import { useState, useEffect } from 'react';
import { MessageSquare, Send, Check } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Skeleton } from '@/components/ui/skeleton';
import { apiFetch, ApiClientError } from '@/lib/api-client';

interface Question {
  _id: string;
  question: string;
  askedByName: string;
  askedAt: string;
  answer: string | null;
  answeredAt: string | null;
}

export function ProductQuestions({ productId }: { productId: string }) {
  const [questions, setQuestions] = useState<Question[]>([]);
  const [loading, setLoading] = useState(true);
  const [newQuestion, setNewQuestion] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [answerText, setAnswerText] = useState<Record<string, string>>({});
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const controller = new AbortController();
    fetch(`/api/market/products/${productId}/questions`, { signal: controller.signal })
      .then((res) => res.ok ? res.json() : { data: [] })
      .then((body) => setQuestions(body.data ?? []))
      .catch(() => {})
      .finally(() => setLoading(false));
    return () => controller.abort();
  }, [productId]);

  async function handleAsk() {
    if (newQuestion.trim().length < 5) return;
    setSubmitting(true);
    setError(null);
    try {
      const res = await apiFetch<{ data: { id: string; question: string } }>(
        `/market/products/${productId}/questions`,
        {
          method: 'POST',
          body: JSON.stringify({ question: newQuestion.trim() }),
          headers: { 'Content-Type': 'application/json' },
          rawResponse: true,
        },
      );
      // Prepend the new question
      setQuestions((prev) => [
        {
          _id: res.data.id,
          question: res.data.question,
          askedByName: 'Tú',
          askedAt: new Date().toISOString(),
          answer: null,
          answeredAt: null,
        },
        ...prev,
      ]);
      setNewQuestion('');
    } catch (err) {
      if (err instanceof ApiClientError && err.status === 401) {
        setError('Inicia sesión para preguntar');
      } else {
        setError('Error al enviar la pregunta');
      }
    } finally {
      setSubmitting(false);
    }
  }

  async function handleAnswer(questionId: string) {
    const text = answerText[questionId]?.trim();
    if (!text) return;
    try {
      await apiFetch(
        `/market/products/${productId}/questions/${questionId}/answer`,
        {
          method: 'POST',
          body: JSON.stringify({ answer: text }),
          headers: { 'Content-Type': 'application/json' },
        },
      );
      // Update local state
      setQuestions((prev) =>
        prev.map((q) =>
          q._id === questionId
            ? { ...q, answer: text, answeredAt: new Date().toISOString() }
            : q,
        ),
      );
      setAnswerText((prev) => ({ ...prev, [questionId]: '' }));
    } catch (err) {
      if (err instanceof ApiClientError) {
        setError(err.message);
      }
    }
  }

  function formatDate(iso: string): string {
    return new Date(iso).toLocaleDateString('es-LA', { day: 'numeric', month: 'short', year: 'numeric' });
  }

  return (
    <Card className="mt-8">
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <MessageSquare className="size-5 text-primary" aria-hidden="true" />
          Preguntas y respuestas
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-4">
        {/* Ask question input */}
        <div className="flex gap-2">
          <Input
            placeholder="Haz una pregunta sobre este producto..."
            value={newQuestion}
            onChange={(e) => setNewQuestion(e.target.value)}
            onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); handleAsk(); } }}
            maxLength={500}
          />
          <Button size="icon" onClick={handleAsk} disabled={submitting || newQuestion.trim().length < 5}>
            <Send className="size-4" aria-hidden="true" />
          </Button>
        </div>
        {error && <p className="text-xs text-destructive">{error}</p>}

        {/* Loading */}
        {loading && (
          <div className="space-y-3">
            {[...Array(3)].map((_, i) => (
              <Skeleton key={i} className="h-20 w-full" />
            ))}
          </div>
        )}

        {/* Empty state */}
        {!loading && questions.length === 0 && (
          <div className="text-center py-6 text-muted-foreground">
            <MessageSquare className="size-8 mx-auto mb-2 opacity-50" aria-hidden="true" />
            <p className="text-sm">No hay preguntas todavía. ¡Sé el primero en preguntar!</p>
          </div>
        )}

        {/* Questions list */}
        {!loading && questions.length > 0 && (
          <div className="space-y-4">
            {questions.map((q) => (
              <div key={q._id} className="border rounded-lg p-4 space-y-2">
                {/* Question */}
                <div>
                  <div className="flex items-center gap-2 mb-1">
                    <span className="text-xs font-medium text-muted-foreground">{q.askedByName}</span>
                    <span className="text-xs text-muted-foreground">· {formatDate(q.askedAt)}</span>
                  </div>
                  <p className="text-sm">{q.question}</p>
                </div>

                {/* Answer (if exists) */}
                {q.answer ? (
                  <div className="ml-6 pl-4 border-l-2 border-primary/20">
                    <div className="flex items-center gap-2 mb-1">
                      <Check className="size-3 text-emerald-500" aria-hidden="true" />
                      <span className="text-xs font-medium text-muted-foreground">Respuesta del vendedor</span>
                      {q.answeredAt && (
                        <span className="text-xs text-muted-foreground">· {formatDate(q.answeredAt)}</span>
                      )}
                    </div>
                    <p className="text-sm text-muted-foreground">{q.answer}</p>
                  </div>
                ) : (
                  /* Answer form (seller only — backend verifies) */
                  <div className="ml-6 pl-4 border-l-2 border-muted">
                    <div className="flex gap-2">
                      <Input
                        placeholder="Escribe una respuesta..."
                        value={answerText[q._id] ?? ''}
                        onChange={(e) => setAnswerText((prev) => ({ ...prev, [q._id]: e.target.value }))}
                        maxLength={1000}
                        className="flex-1"
                      />
                      <Button
                        size="sm"
                        onClick={() => handleAnswer(q._id)}
                        disabled={!(answerText[q._id]?.trim()?.length > 0)}
                      >
                        Responder
                      </Button>
                    </div>
                  </div>
                )}
              </div>
            ))}
          </div>
        )}
      </CardContent>
    </Card>
  );
}
